import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import Layout from "@/components/Layout";
import PlanCard from "@/components/PlanCard";
import api from "@/lib/api";
import { prefetchPlan, setCachedPlan } from "@/lib/planCache";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Sparkles, X, RotateCw } from "lucide-react";
import { toast } from "sonner";

const FALLBACK_STYLES = ["Modern", "Farmhouse", "Traditional", "Contemporary", "Mediterranean", "Craftsman", "Ranch", "Colonial", "Villa", "Cottage"];

// ─────────────────────────────────────────────────────────────────────
// Module-level marketplace cache.
// Survives component unmount (React Router remounts on back-nav) so:
//   • Back-nav restores the previous grid INSTANTLY — no "Loading…" flash.
//   • Filters + scroll position are preserved per unique filter combo.
//   • Stale-while-revalidate: cached data shown immediately while we
//     refetch silently in the background. Empty state only rendered
//     once the API has explicitly confirmed 0 matches (never on error
//     or during a refetch).
// ─────────────────────────────────────────────────────────────────────
const _marketplaceCache = new Map();  // key → { plans, ts, scrollY }
const CACHE_TTL_MS = 5 * 60 * 1000;   // 5 min — fresh enough for marketplace catalog

const _buildKey = (params) => JSON.stringify(params || {});

const Plans = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const style = searchParams.get("style") || "All";
  const minBedrooms = searchParams.get("min_bedrooms") || "";
  const search = searchParams.get("search") || "";

  // Iter57 note: dedicated SEO landing pages live at /plans/style/{slug},
  // /plans/category/{slug}, /plans/collection/{slug}. Query-string filters
  // on /plans (e.g. /plans?style=Modern) stay on the browse page — no
  // auto-redirect — so the filter dropdowns work as expected.

  // Compute the cache key BEFORE first render so we can hydrate synchronously.
  const params = {};
  if (style && style !== "All") params.style = style;
  if (minBedrooms) params.min_bedrooms = minBedrooms;
  if (search) params.search = search;
  const cacheKey = _buildKey(params);
  const cached = _marketplaceCache.get(cacheKey);

  // If we already have cached plans for this key → hydrate immediately.
  // This is what makes back-nav feel instant.
  const [plans, setPlans] = useState(() => cached?.plans || []);
  const [confirmed, setConfirmed] = useState(() => !!cached); // API has confirmed at least once
  const [refreshing, setRefreshing] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiQuery, setAiQuery] = useState("");
  const requestKeyRef = useRef(cacheKey);

  // Iter55c — dynamic Style filter driven by /api/styles (Styles Master).
  // Falls back to a compiled list only during the tiny window before the
  // first fetch resolves so the dropdown is never empty.
  const [styles, setStyles] = useState(FALLBACK_STYLES);
  useEffect(() => {
    api.get("/styles")
      .then((r) => {
        const names = Array.isArray(r.data?.styles) ? r.data.styles : [];
        if (names.length > 0) setStyles(names);
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  // Fetch effect — stale-while-revalidate.
  useEffect(() => {
    const key = cacheKey;
    requestKeyRef.current = key;
    const existing = _marketplaceCache.get(key);
    const isFresh = existing && (Date.now() - existing.ts) < CACHE_TTL_MS;

    // If cached is fresh, skip background refetch entirely.
    if (isFresh) {
      setPlans(existing.plans);
      setConfirmed(true);
      return;
    }

    // If we have any cached data (even stale), show it while we refetch.
    if (existing) {
      setPlans(existing.plans);
      setConfirmed(true);
    }

    setRefreshing(true);
    api.get("/plans", { params })
      .then((r) => {
        // Ignore late responses whose params no longer match the current view.
        if (requestKeyRef.current !== key) return;
        const data = Array.isArray(r.data) ? r.data : [];
        // Defensive SWR: never let a background refetch REPLACE a previously
        // successful non-empty grid with an empty one. If the API returns []
        // when we already have cards for these same params, keep the cards.
        // This prevents the "0 Results / No Matches" flash the user reported.
        const prev = existing?.plans || [];
        if (data.length === 0 && prev.length > 0) {
          // Bump timestamp but don't overwrite plans.
          _marketplaceCache.set(key, { plans: prev, ts: Date.now(), scrollY: existing?.scrollY || 0 });
          setConfirmed(true);
          return;
        }
        _marketplaceCache.set(key, { plans: data, ts: Date.now(), scrollY: existing?.scrollY || 0 });
        setPlans(data);
        setConfirmed(true);
        // Pre-warm the plan-detail cache for the top plans so the first
        // click into ANY of them navigates instantly.
        data.slice(0, 6).forEach((p) => {
          if (p?.id) setCachedPlan(p.id, p);
        });
      })
      .catch(() => {
        // Silent — keep whatever plans we already have. Do NOT clear.
        // If we had nothing cached, `plans` stays [] but `confirmed` also
        // stays false — so the UI shows a spinner rather than "0 Results".
      })
      .finally(() => {
        if (requestKeyRef.current === key) setRefreshing(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  // Scroll restore — after content is in the DOM.
  useEffect(() => {
    const saved = _marketplaceCache.get(cacheKey)?.scrollY;
    if (saved && plans.length > 0) {
      // Give layout a beat to settle before snapping.
      const t = setTimeout(() => window.scrollTo({ top: saved, behavior: "instant" in window ? "instant" : "auto" }), 0);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans.length > 0, cacheKey]);

  // Persist scroll position continuously (throttled) so back-nav from PlanDetail
  // uses the freshest position. Also saves on unmount as a safety net.
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const entry = _marketplaceCache.get(cacheKey);
        if (entry) entry.scrollY = window.scrollY;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
      const entry = _marketplaceCache.get(cacheKey);
      if (entry) entry.scrollY = window.scrollY;
    };
  }, [cacheKey, location.pathname]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "All") next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const clearAll = () => setSearchParams({});

  const runAiSearch = async () => {
    if (!aiQuery.trim()) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/search", { query: aiQuery });
      const results = Array.isArray(data.plans) ? data.plans : [];
      setPlans(results);
      setConfirmed(true);
      // AI results are a synthetic view — cache under a synthetic key so
      // filter reset restores the filter-based view.
      _marketplaceCache.set(`ai:${aiQuery.trim()}`, { plans: results, ts: Date.now(), scrollY: 0 });
      toast.success("AI search complete", { description: `${results.length} plans matched.` });
    } catch (e) {
      toast.error("AI search failed", { description: e.response?.data?.detail || "Try again." });
    } finally {
      setAiLoading(false);
    }
  };

  // Only render "Loading…" when we have literally nothing (first ever visit
  // to these params + no cache). If we have any results, render them — even
  // stale ones during a refetch. If confirmed=false and plans=[] and !refreshing,
  // we've never loaded — show a first-load spinner instead of "0 Results".
  const initialLoad = !confirmed && plans.length === 0;
  const showEmptyState = confirmed && plans.length === 0;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-10">
          <div className="lg:col-span-7">
            <div className="overline text-accent">The Collection</div>
            <h1 className="font-display text-5xl lg:text-6xl mt-3 leading-none tracking-tighter">All plans</h1>
            <p className="mt-4 text-muted-foreground">Filter by style, size, or use natural-language AI search.</p>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-border bg-background p-4">
              <div className="overline text-accent mb-2 flex items-center gap-2">
                <Sparkles size={12} /> AI-powered search
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="ai-search-input"
                  placeholder='"4 bedroom farmhouse under 3000 sqft"'
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runAiSearch()}
                  className="rounded-none border-foreground/20"
                />
                <Button data-testid="ai-search-btn" onClick={runAiSearch} disabled={aiLoading} className="rounded-none bg-primary">
                  {aiLoading ? "…" : <Search size={16} />}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div data-testid="filters-bar" className="flex flex-wrap gap-3 items-center border-y border-border py-4 mb-10">
          <span className="overline text-muted-foreground">Filter:</span>
          <Select value={style} onValueChange={(v) => updateParam("style", v)}>
            <SelectTrigger data-testid="filter-style" className="w-40 rounded-none">
              <SelectValue placeholder="Style" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem key="All" value="All">All</SelectItem>
              {styles.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={minBedrooms} onValueChange={(v) => updateParam("min_bedrooms", v)}>
            <SelectTrigger data-testid="filter-bedrooms" className="w-40 rounded-none">
              <SelectValue placeholder="Bedrooms" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1+ bd</SelectItem>
              <SelectItem value="2">2+ bd</SelectItem>
              <SelectItem value="3">3+ bd</SelectItem>
              <SelectItem value="4">4+ bd</SelectItem>
              <SelectItem value="5">5+ bd</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <Input
              data-testid="filter-search"
              placeholder="Search…"
              defaultValue={search}
              onKeyDown={(e) => e.key === "Enter" && updateParam("search", e.target.value)}
              className="w-56 rounded-none"
            />
          </div>

          {(style !== "All" || minBedrooms || search) && (
            <Button variant="ghost" size="sm" onClick={clearAll} data-testid="clear-filters" className="rounded-none">
              <X size={14} className="mr-1" /> Clear
            </Button>
          )}

          <span className="ml-auto text-sm text-muted-foreground flex items-center gap-2" data-testid="results-count">
            {refreshing && plans.length > 0 && (
              <RotateCw size={12} className="animate-spin" data-testid="refreshing-indicator" />
            )}
            {initialLoad
              ? "Loading…"
              : `${plans.length} result${plans.length === 1 ? "" : "s"}`}
          </span>
        </div>

        <div data-testid="plans-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {initialLoad && (
            <div className="col-span-full flex items-center justify-center py-24" data-testid="marketplace-initial-loading">
              <div className="text-muted-foreground text-sm">Loading plans…</div>
            </div>
          )}
          {plans.map((p) => <PlanCard key={p.id} plan={p} />)}
          {showEmptyState && (
            <div className="col-span-full text-center py-24">
              <div className="overline text-accent">No matches</div>
              <p className="font-display text-3xl mt-3">Try broader filters</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

// Expose the cache for debug/tests (no-op in production).
if (typeof window !== "undefined") {
  window.__dheMarketplaceCache = _marketplaceCache;
}

export default Plans;
